import { type ReactNode } from "react";

import { Button } from "@/web/components/primitives/button";
import { ErrorHandler } from "@/web/lib/errors";
import { useReducerMutation } from "@/web/lib/tanstack";
import { reducers } from "@tycho/web-bindings";

import { useAcknowledgments } from "./AcknowledgmentsProvider";
import { type MessageKey } from "./messageKeys";
import { AckDialogShell } from "./shells";

/**
 * State-derived ack-gated modal. Renders only when:
 *   - the caller-supplied `when` precondition is true,
 *   - the orientation tour has been dismissed (the tour owns the foreground
 *     until acked — see MESSAGE_KEYS.orientation), and
 *   - the user has not already acked `messageKey`.
 *
 * Clicking "acknowledge" writes a row via the `acknowledgeMessage` reducer;
 * the next super-view tick removes the modal because the ack is now present.
 */
export function AcknowledgmentModal({
  messageKey,
  when = true,
  title,
  body,
  acknowledgeLabel = "Got it",
}: {
  messageKey: MessageKey;
  when?: boolean;
  title: ReactNode;
  body: ReactNode;
  acknowledgeLabel?: string;
}) {
  const { isAcked, shouldDefer, underwritableEntityId } = useAcknowledgments();

  const { mutateAsync: acknowledge, isPending } = useReducerMutation(reducers.acknowledgeMessage, {
    onError: ErrorHandler.default,
  });

  if (!when || shouldDefer(messageKey) || isAcked(messageKey)) return null;

  const onAcknowledge = () => {
    void acknowledge({ underwritableEntityId, messageKey });
  };

  return (
    <AckDialogShell
      title={title}
      body={body}
      footer={
        <Button onClick={onAcknowledge} disabled={isPending} color="blue">
          {acknowledgeLabel}
        </Button>
      }
    />
  );
}
